import type { ChatCitation } from '../types/chatStream';
import {
  mcgrawHillReferenceByCiteKey,
  mcgrawHillReferenceById,
  type McGrawHillReferenceRole,
} from './mcgrawHillReferences';

/**
 * mcgraw-hill: numbered sources. The AI desktop cites the reference documents
 * with `[[cite:<cite key>:<page>:<heading>]]` markers after its claims; the
 * backend turns each into a `chat_document_page` citation whose label is the
 * heading (when it is really on that page) or "p. N". Here the markers are
 * numbered in the order they first appear, each becomes a `[[cite N]]` badge
 * token the answer renderer draws, and the citations become the numbered
 * Sources list under the answer.
 */
export interface McGrawHillSource {
  number: number;
  citation: ChatCitation;
}

const CITE_MARKER = /\[\[cite:([A-Za-z0-9-]+):(\d+)(?::([^\]\n]*))?\]\]/g;
/** A citation marker the stream has only partly delivered, at the very end. */
const PARTIAL_CITE_AT_END = /\[\[(?:c(?:i(?:t(?:e(?::[^\]\n]*\]?)?)?)?)?)?$/;

const compact = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, '');

/** The chapter number a reference title carries ("Chapter 265"). */
export const chapterOfTitle = (title: string | undefined): string | null =>
  /\bCh(?:apter|\.)?\s*(\d+)/i.exec(title ?? '')?.[1] ?? null;

/** Whether a citation is the document a marker's cite key names. */
const isCitedDocument = (citation: ChatCitation, key: string): boolean => {
  const reference = mcgrawHillReferenceById(citation.documentId);
  return reference
    ? reference.citeKey.toLowerCase() === key.toLowerCase()
    : chapterOfTitle(citation.title) === key;
};

/** A marker's badge colour: its document's role (client when unknown). */
const roleOfKey = (key: string): McGrawHillReferenceRole =>
  mcgrawHillReferenceByCiteKey(key)?.role ?? 'client';

/** Whether an answer carries citation markers (or a half-streamed one). */
export const hasCiteMarkers = (text: string): boolean =>
  text.includes('[[cite:') || PARTIAL_CITE_AT_END.test(text);

/**
 * The answer with its markers as numbered badge tokens, and the numbered
 * sources: the pages the answer cites, in the order it first cites them. A
 * marker no source matches is dropped, and a source no marker names is left
 * off the list.
 */
export const numberMcGrawHillSources = (
  text: string,
  citations: readonly ChatCitation[]
): {
  text: string;
  sources: McGrawHillSource[];
  /** Each badge number's colour. */
  roles: Map<number, McGrawHillReferenceRole>;
} => {
  const roles = new Map<number, McGrawHillReferenceRole>();

  const pages = citations.filter(
    (citation) => citation.kind === 'chat_document_page'
  );

  // Still streaming: the sources come with the finished answer, so the
  // badges are numbered by marker in the meantime (the same order they keep).
  if (pages.length === 0) {
    const provisional = new Map<string, number>();
    return {
      text: text
        .replace(PARTIAL_CITE_AT_END, '')
        .replace(
          CITE_MARKER,
          (_marker, citeKey: string, page: string, heading?: string) => {
            const key = `${citeKey.toLowerCase()}:${page}:${compact(heading ?? '')}`;
            if (!provisional.has(key))
              provisional.set(key, provisional.size + 1);
            const number = provisional.get(key) ?? 0;
            roles.set(number, roleOfKey(citeKey));
            return `[[cite ${number}]]`;
          }
        ),
      sources: [],
      roles,
    };
  }

  const numbers = new Map<string, number>();
  const sources: McGrawHillSource[] = [];

  const numberOf = (citation: ChatCitation): number => {
    const existing = numbers.get(citation.id);
    if (existing) return existing;
    const number = sources.length + 1;
    numbers.set(citation.id, number);
    sources.push({ number, citation });
    return number;
  };

  const numbered = text
    .replace(PARTIAL_CITE_AT_END, '')
    .replace(
      CITE_MARKER,
      (_marker, citeKey: string, page: string, heading?: string) => {
        const onPage = pages.filter(
          (citation) =>
            citation.page === Number(page) && isCitedDocument(citation, citeKey)
        );

        const wanted = heading ? compact(heading) : '';

        const match =
          onPage.find(
            (citation) => wanted && compact(citation.label ?? '') === wanted
          ) ?? onPage[0];

        if (!match) return '';
        const number = numberOf(match);
        roles.set(number, roleOfKey(citeKey));
        return `[[cite ${number}]]`;
      }
    );

  return { text: numbered, sources, roles };
};

/** The answer without its markers or badge tokens, for Copy and read-aloud. */
export const stripCiteMarkers = (text: string): string =>
  text
    .replace(PARTIAL_CITE_AT_END, '')
    .replace(CITE_MARKER, '')
    .replace(/\s?\[\[cite \d+\]\]/g, '');

/**
 * "Harrison's 22e, Chapter 265 Heart Failure: Management" as its book (bold
 * in the list) and the rest.
 */
export const splitSourceTitle = (
  title: string | undefined
): { book: string; rest: string } => {
  const value = title ?? 'Reference';
  const comma = value.indexOf(', ');
  return comma > 0
    ? { book: value.slice(0, comma), rest: value.slice(comma) }
    : { book: value, rest: '' };
};
